<template>
  <form @submit.prevent="submitStep3">
    <div class="grid gap-24">
      <CheckoutStageComponentsContainer>
        <h2 class="heading-xs">Dina uppgifter</h2>

        <template
          v-if="
            !checkoutStore.fetchedData.address ||
            checkoutStore.ui.customerRequiresAuth === true
          "
        >
          <CheckoutBankIdSSNValidation @on-auth="onAuthorizedCustomer" />
        </template>

        <div
          ref="screenReaderAddressNotificationRef"
          aria-live="polite"
          aria-atomic="true"
        ></div>

        <div
          v-show="
            (checkoutStore.ui.customerRequiresAuth === false &&
              !!checkoutStore.fetchedData.address) ||
            checkoutStore.ui.loading.isStep3AddressLoading
          "
        >
          <CheckoutShowRetrievedAddress />
          <CheckoutEmailAndConsentInputs :key="emailAndConsentRenderKey" />
        </div>
      </CheckoutStageComponentsContainer>

      <CheckoutStageComponentsContainer v-if="shouldShowSelectShipping">
        <CheckoutSelectShippingMethod />
      </CheckoutStageComponentsContainer>

      <GlobalNotificationContainer
        :show="!!notifications.title"
        :is-just-text-row="true"
      />

      <CheckoutNextStepButton
        v-if="!!checkoutStore.fetchedData.address"
        :show-loading="isSubmitting"
        :disabled="checkoutStore.isStep3ProceedBlocked"
        @click="addShippingInfoEvent"
      >
        Gå vidare
      </CheckoutNextStepButton>
    </div>
  </form>
</template>

<script lang="ts" setup>
import {
  STEP3_ADDRESS_PERSONAL_EMAIL,
  type Step3FormValues,
} from '~/models/checkout/forms/Step3Form.model';
import type { DeliveryAddress } from '~/models/DeliveryAddress.model';
import type { HalebopRequestErrorData } from '~/repository/factory';
import {
  INSTABOX_SYSTEM_NAME,
  POSTNORD_SYSTEM_NAME,
  type ShippingMethod,
} from '~/repository/modules/ShippingMethod.module';

const checkoutStore = useMyCheckoutStore();
const notifications = useNotificationStore();
const screenReaderAddressNotificationRef = ref<HTMLDivElement | null>(null);
const { pushAddShippingInfoEvent } = useGoogleAnalyticsEvents();
const { $api } = useNuxtApp();

const addShippingInfoEvent = () => {
  if (checkoutStore.trackingData.selectedShipping) {
    pushAddShippingInfoEvent(checkoutStore.trackingData.selectedShipping);
  }
};

const {
  fetchShippingMethods,
  fetchDeliveryAddress,
  fetchSsnCensored,
  setUserInput,
} = useFetchAddressAndShipping();

const initialValues: Step3FormValues = {
  step3_shipping_methods_radio_group: undefined,
  step3_shipping_instabox_phone_number:
    checkoutStore.userState.inputtedInstaboxPhoneNumber,
  step3_address_personal_email: checkoutStore.userState.newEmailAddress,
};

const {
  handleSubmit,
  isSubmitting,
  errorBag,
  isValidating,
  setFieldValue,
  isFieldTouched,
} = useForm<Step3FormValues>({
  initialValues,
});

const addressWatchable = computed(() => checkoutStore.fetchedData.address);
const emailAndConsentRenderKey = ref(0);

watch(
  addressWatchable,
  (value) => {
    if (value && value.Email && !isFieldTouched(STEP3_ADDRESS_PERSONAL_EMAIL)) {
      checkoutStore.userState.newEmailAddress = value.Email;
      setFieldValue(STEP3_ADDRESS_PERSONAL_EMAIL, value.Email);
      emailAndConsentRenderKey.value += 1;
    }
  },
  { immediate: true },
);

watch(isValidating, (newIsValidating, oldIsValidating) => {
  if (!newIsValidating && oldIsValidating) {
    const fieldsWithError = Object.keys(errorBag.value);
    if (fieldsWithError.length === 0) {
      return;
    }

    const firstErrorFieldName = fieldsWithError[0];
    const element = document.getElementById(firstErrorFieldName);
    if (element) {
      element.focus();
    }
  }
});

const shouldShowSelectShipping = computed(() => {
  if (
    checkoutStore.fetchedData.shippingOptions.length === 1 &&
    checkoutStore.fetchedData.shippingOptions.at(0)
      ?.ShippingMethodSystemName === POSTNORD_SYSTEM_NAME
  ) {
    return false;
  }
  return (
    !checkoutStore.ui.loading.isStep3ShippingLoading &&
    !!checkoutStore.fetchedData.address &&
    checkoutStore.fetchedData.shippingOptions.length !== 0
  );
});

const onAuthorizedCustomer = async (response?: {
  isUsingBankIdReplacement: boolean;
  testPersonaSsn?: string;
}) => {
  const addressResponse = await fetchDeliveryAddress(
    response?.testPersonaSsn,
    response?.isUsingBankIdReplacement ?? false,
  );

  if (!addressResponse) {
    return;
  }

  if (notifications.type === 'error') {
    notifications.reset();
  }

  fetchSsnCensored();
  onAddressResponse(addressResponse);
};

const onAddressResponse = async (addressResponse?: DeliveryAddress) => {
  checkoutStore.fetchedData.address = addressResponse;

  if (addressResponse && addressResponse.Email) {
    setFieldValue('step3_address_personal_email', addressResponse.Email);
  }
  if (addressResponse && screenReaderAddressNotificationRef.value) {
    const notificationElement = document.createElement('p');
    notificationElement.textContent = `Din address har hämtats och visas nedan, ${addressResponse?.FirstName} ${addressResponse?.LastName}, ${addressResponse?.ZipPostalCode} ${addressResponse?.City}`;
    notificationElement.classList.add('sr-only');
    screenReaderAddressNotificationRef.value.appendChild(notificationElement);
  }

  const shippingOpsResponse = await fetchShippingMethods();
  checkoutStore.fetchedData.shippingOptions = shippingOpsResponse;

  if (!checkoutStore.userState.selectedDeliveryMethod) {
    checkoutStore.userState.selectedDeliveryMethod = shippingOpsResponse[0];
  }
};

const reset = () => {
  checkoutStore.fetchedData.address = undefined;
  checkoutStore.fetchedData.shippingOptions = [];

  checkoutStore.ui.customerRequiresAuth = true;
};

const submitStep3 = handleSubmit(async (formValues) => {
  if (checkoutStore.isStep3ProceedBlocked) {
    return;
  }

  try {
    let selectedShippingMethod: ShippingMethod | undefined = undefined;

    if (!shouldShowSelectShipping.value) {
      selectedShippingMethod = checkoutStore.fetchedData.shippingOptions.at(0);
    } else if (formValues.step3_shipping_methods_radio_group) {
      selectedShippingMethod = formValues.step3_shipping_methods_radio_group;
    }

    if (!checkoutStore.fetchedData.address) {
      throw new Error('Ingen adress kunde hittas. Vänligen försök igen.', {
        cause: 'app',
      });
    }

    checkoutStore.userState.newEmailAddress =
      formValues.step3_address_personal_email;

    if (
      !selectedShippingMethod &&
      checkoutStore.ui.loading.isStep3ShippingLoading
    ) {
      throw new Error(
        'Bekräftar fortfarande leverans, vänligen tryck igen om en stund.',
        { cause: 'app' },
      );
    } else if (!selectedShippingMethod) {
      throw new Error('Inget leveranssätt valt.', { cause: 'app' });
    }

    if (
      selectedShippingMethod.ShippingMethodSystemName ===
        INSTABOX_SYSTEM_NAME &&
      !formValues.step3_shipping_instabox_phone_number
    ) {
      throw new Error('Instabox kräver ett telefonnummer för utskick.', {
        cause: 'app',
      });
    }

    await setUserInput(
      selectedShippingMethod,
      formValues.step3_address_personal_email,
    );

    if (notifications.type === 'error') {
      notifications.reset();
    }

    checkoutStore.goToNextStep();
  } catch (error) {
    if (isNuxtError<HalebopRequestErrorData>(error) && error.message) {
      notifications.setErrorNotificationFromCatchClause(
        error.message,
        'Kunde inte gå vidare.',
      );

      logError(error);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorMessage = (error as any).message;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorCause = (error as any).cause;

    const message =
      errorMessage && errorCause === 'app' ? errorMessage : undefined;
    notifications.setErrorNotificationFromCatchClause(message);
  }
});

onMounted(async () => {
  if (screenReaderAddressNotificationRef.value) {
    screenReaderAddressNotificationRef.value.innerHTML = '';
  }

  if (checkoutStore.ui.customerRequiresAuth === false) {
    onAuthorizedCustomer();
  }

  try {
    const isAuthRequiredUpdate =
      await $api.CustomerModule.isCustomerBankIDVerificationRequired();

    if (isAuthRequiredUpdate) {
      reset();
    }
  } catch (e) {
    // nothing
  }
});
</script>

<style lang="scss" scoped></style>
